import React, { useEffect, useState } from "react";
import { graphql, useMutation } from "react-relay";

import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";

import COLOR from "lib/colors";

import { CheckCircleOutlinedIcon } from "common/DS/Icons";
import TextField from "common/DS/TextField";

import { ContactInfoFieldRequestMutation as ContactInfoFieldRequestMutationType } from "__generated__/ContactInfoFieldRequestMutation.graphql";
import { ContactInfoFieldVerifyMutation as ContactInfoFieldVerifyMutationType } from "__generated__/ContactInfoFieldVerifyMutation.graphql";

const ContactInfoFieldRequestMutation = graphql`
    mutation ContactInfoFieldRequestMutation($type: ContactInfoType!, $value: String!) {
        requestContactInfoVerification(type: $type, value: $value) {
            contactInfo {
                id
                type
                value
                state
            }
        }
    }
`;

const ContactInfoFieldVerifyMutation = graphql`
    mutation ContactInfoFieldVerifyMutation($type: ContactInfoType!, $code: String!) {
        verifyContactInfo(type: $type, code: $code) {
            ... on ContactInfoMutationResult {
                contactInfo {
                    id
                    type
                    value
                    state
                }
                me {
                    id
                    email
                    phone
                }
            }
            ... on ContactInfoVerifyError {
                message
            }
        }
    }
`;

type ContactType = "EMAIL" | "PHONE";

type Props = {
    type: ContactType;
    label: string;
    initialValue: string;
    verifiedInitial: Maybe<string>;
};

const ContactInfoField = ({ type, label, initialValue, verifiedInitial }: Props) => {
    const [value, setValue] = useState(initialValue);
    const [verifiedValue, setVerifiedValue] = useState(verifiedInitial);
    const [originalValue, setOriginalValue] = useState(initialValue);
    const [edited, setEdited] = useState(false);
    const [awaitingCode, setAwaitingCode] = useState(false);
    const [code, setCode] = useState("");
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setError(null);
    }, [value, code]);

    const [requestCode, requesting] = useMutation<ContactInfoFieldRequestMutationType>(
        ContactInfoFieldRequestMutation,
    );
    const [verifyCode, verifying] = useMutation<ContactInfoFieldVerifyMutationType>(
        ContactInfoFieldVerifyMutation,
    );

    const onSendCode = () => {
        setError(null);
        requestCode({
            variables: { type, value: value.trim() },
            onCompleted: (resp) => {
                if (!resp.requestContactInfoVerification) {
                    setError("Could not send code, try again.");
                    return;
                }
                setAwaitingCode(true);
                setCode("");
                setEdited(false);
            },
            onError: () => setError("Could not send code, try again."),
        });
    };

    const onConfirm = () => {
        if (!awaitingCode) return;
        setError(null);
        verifyCode({
            variables: { type, code: code.trim() },
            onCompleted: (resp) => {
                const result = resp.verifyContactInfo;
                if (!result) {
                    setError("Could not verify, try again.");
                    return;
                }
                if (result.contactInfo) {
                    setVerifiedValue(result.contactInfo.value);
                    setOriginalValue(result.contactInfo.value);
                    setAwaitingCode(false);
                    setCode("");
                    setEdited(false);
                } else if (result.message) {
                    setError(result.message);
                }
            },
            onError: () => setError("Could not verify, try again."),
        });
    };

    const onUseDifferent = () => {
        setAwaitingCode(false);
        setCode("");
        setError(null);
        setEdited(true);
    };

    const inCodeStep = awaitingCode;
    const showVerify = !inCodeStep && edited && !!value.trim();
    const dataTest = `contact-info-${type.toLowerCase()}`;
    let valueAdornment: React.ReactNode = null;
    if (requesting) {
        valueAdornment = <CircularProgress size={20} />;
    } else if (!edited && value && value.trim() === verifiedValue) {
        valueAdornment = <CheckCircleOutlinedIcon sx={{ color: COLOR["green-6"] }} />;
    }

    return (
        <Stack spacing={1}>
            <div className="flex flex-ac gap-sm">
                <TextField
                    label={label}
                    InputLabelProps={{ shrink: true }}
                    className="flex-grow"
                    value={value}
                    disabled={inCodeStep || requesting}
                    onChange={(e) => {
                        setValue(e.target.value);
                        setEdited(true);
                    }}
                    onBlur={() => {
                        if (value.trim() === originalValue) setEdited(false);
                    }}
                    InputProps={{
                        inputProps: { "data-test": `${dataTest}-value` },
                        endAdornment: valueAdornment ? (
                            <InputAdornment position="end">{valueAdornment}</InputAdornment>
                        ) : undefined,
                    }}
                />
                {showVerify && (
                    <Button onClick={onSendCode} variant="text">
                        Verify
                    </Button>
                )}
            </div>
            {inCodeStep && (
                <>
                    <TextField
                        label={`Enter the Code from your ${label}`}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        InputProps={{
                            inputProps: { "data-test": `${dataTest}-code` },
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Button
                                        size="small"
                                        onClick={onConfirm}
                                        disabled={verifying || code.trim().length !== 6}
                                    >
                                        Confirm
                                    </Button>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <div className="flex gap-md">
                        <Link
                            component="button"
                            onClick={onSendCode}
                            sx={{ color: COLOR["blue-5"], fontSize: 12, textDecoration: "none" }}
                        >
                            Send another code
                        </Link>
                        <Link
                            component="button"
                            onClick={onUseDifferent}
                            sx={{ color: COLOR["blue-5"], fontSize: 12, textDecoration: "none" }}
                        >
                            Use a different {type === "EMAIL" ? "email" : "phone"}
                        </Link>
                    </div>
                </>
            )}
            {error && <div className="color-red">{error}</div>}
        </Stack>
    );
};

export default ContactInfoField;
